import type {
  EquitiesSentimentPacket,
  EquitiesSentimentSourceState,
} from "../../types/equities-sentiment";
import type {
  TiltScoutEvidence,
  TiltScoutProof,
  TiltScoutSnapshot,
} from "./tilt-scout-types";
import {
  normalizeTiltScoutReadLabel,
  normalizeTiltScoutText,
} from "./tilt-scout-copy";

export function packetToTiltScoutSnapshot(
  packet: EquitiesSentimentPacket,
  selectedSymbol: string,
): TiltScoutSnapshot {
  return {
    selectedSymbol,
    canonicalSymbol: packet.canonicalSymbol,
    score: packet.score,
    posture: packet.posture,
    label: normalizeTiltScoutReadLabel(packet.label),
    summary: normalizeTiltScoutText(packet.summary),
    confidenceText: normalizeTiltScoutText(packet.confidenceText),
    rangeText: normalizeTiltScoutText(packet.rangeText),
    invalidationText: normalizeTiltScoutText(packet.invalidationText),
    eventText: normalizeTiltScoutText(packet.eventText),
    topEvidence: toEvidence(packet),
    recentRelatedCatalysts: packet.evidence
      .map((evidence) => toEvidenceItem(packet, evidence))
      .filter(
        (item) =>
          (item.headlineImpactWeight ?? item.headlineWeight ?? item.score) > 7,
      )
      .slice(0, 3),
    proofs: packet.proofs.map((proof): TiltScoutProof => ({
      label: proof.label,
      status: toProofStatus(proof.status),
      detail: normalizeTiltScoutText(proof.detail),
    })),
    axisScores: packet.axisScores,
    sourceState: packet.sourceState,
    whatWouldChange: packet.whatWouldChange.map((item) =>
      normalizeTiltScoutText(item),
    ),
    marketPerformance: packet.marketPerformance ?? null,
    deskPlanPreview: {
      state: packet.deskPlanPreview.state,
      eventText: normalizeTiltScoutText(packet.deskPlanPreview.eventText),
      windowText: normalizeTiltScoutText(packet.deskPlanPreview.windowText),
      forecastText: normalizeTiltScoutText(packet.deskPlanPreview.forecastText),
      changeText: normalizeTiltScoutText(packet.deskPlanPreview.changeText),
    },
    updatedAt: packet.updatedAt,
  };
}

function toProofStatus(
  status: EquitiesSentimentSourceState,
): TiltScoutProof["status"] {
  if (status === "fixture") return "partial";
  return status;
}

function toEvidence(packet: EquitiesSentimentPacket): TiltScoutEvidence | null {
  const evidence = packet.evidence[0];
  if (!evidence) return null;
  return toEvidenceItem(packet, evidence);
}

function toEvidenceItem(
  packet: EquitiesSentimentPacket,
  evidence: EquitiesSentimentPacket["evidence"][number],
): TiltScoutEvidence {
  return {
    id: evidence.id,
    title: normalizeTiltScoutText(evidence.title),
    source: evidence.source,
    score: evidence.score ?? 0,
    headlineWeight: evidence.headlineWeight ?? null,
    headlineIvScore: evidence.headlineIvScore ?? evidence.score ?? null,
    headlineEventWeight: evidence.headlineEventWeight ?? null,
    headlineFreshness: evidence.headlineFreshness ?? null,
    headlineImpactWeight: evidence.headlineImpactWeight ?? null,
    headlineWeightSource: evidence.headlineWeightSource,
    direction: evidence.direction,
    publishedAt: evidence.publishedAt ?? packet.updatedAt,
  };
}
